import type { GoogleFontItem } from 'src/types';
import { queryOptions } from '@tanstack/react-query';

import { getOrganizationGoogleFonts as endpoint } from 'admin/resources';
import queryClient from 'admin/queries/client';
import { organizationKeys } from 'admin/queries/queryKeys';

type OrganizationGoogleFonts = {
	items: GoogleFontItem[];
	kind: string;
};

const FIVE_MINUTES = 5 * 60 * 1000;
const TRANSIENT_NETWORK_CODES = new Set([
	'ECONNRESET',
	'ECONNREFUSED',
	'EPIPE',
	'ENOTFOUND',
	'ENETUNREACH',
	'EAI_AGAIN',
]);
const TRANSIENT_HTTP_STATUSES = new Set([408, 429, 502, 503, 504]);

type RequestError = {
	code?: string;
	crossDomain?: boolean;
	status?: number;
};

const shouldRetry = (failureCount: number, error: unknown) => {
	if (failureCount >= 1 || typeof error !== 'object' || error === null) return false;

	const requestError = error as RequestError;
	return (
		requestError.crossDomain === true ||
		(typeof requestError.code === 'string' && TRANSIENT_NETWORK_CODES.has(requestError.code)) ||
		(typeof requestError.status === 'number' && TRANSIENT_HTTP_STATUSES.has(requestError.status))
	);
};

export const getOrganizationGoogleFontsQueryOptions = (organizationId: string) =>
	queryOptions({
		queryKey: organizationKeys.googleFonts(organizationId),
		queryFn: async ({ signal }) => {
			const response = await endpoint
				.params({ organizationId })
				.send<ApiV1Response<OrganizationGoogleFonts>>(undefined, { signal });
			const items = response.body?.result?.items;
			if (!response.body?.success || !Array.isArray(items)) {
				throw new Error('Google Fonts are unavailable.');
			}
			return items;
		},
		retry: shouldRetry,
		staleTime: FIVE_MINUTES,
	});

export const fetchOrganizationGoogleFonts = (organizationId: string) =>
	queryClient.fetchQuery(getOrganizationGoogleFontsQueryOptions(organizationId));
